import { Skeleton } from '@/components/ui/skeleton';
import {
  LLM_PROFILES_PLACEHOLDER_ROW_COUNT,
  LLM_PROFILES_TABLE_COLUMN_COUNT,
} from '@/features/organization-admin';
import { PendingRegion } from '@/shared/components/PendingRegion';

/**
 * The LLM profiles grid while its read lands (spec 136 FR-014).
 *
 * Shaped like `LimitsTableSkeleton`, and for the reason `organization-admin`'s
 * `adminLayout.ts` sets out: the column count comes from the shared constant — reached
 * through that feature's barrel — rather than from a `10` typed here, so
 * the guard in `tests/shared/pendingSurface.test.ts` reaches this width too and an
 * eleventh column cannot leave the placeholder silently one bar short.
 *
 * It draws no content — no text, no icons, no values (FR-015). A placeholder is the
 * shape of what is coming, and a drawing of the thing is a small lie that has to be
 * maintained. The row count is fixed and short for the same reason the console's are:
 * the true count is unknowable while pending, and content growing downward into empty
 * space disturbs less than content pulling upward past the fold (research D7).
 *
 * It declares no responsive grid of its own and writes no status role of its own — the
 * announcement belongs entirely to `PendingRegion`, which carries the whole
 * accessibility contract, and both rules are enforced by name on every `*Skeleton*`
 * module. (The guard reads this file as text, comments included, so the role is named
 * in prose rather than quoted here.)
 */
export function LlmProfilesTableSkeleton() {
  return (
    <PendingRegion label="Loading LLM profiles" className="gap-3">
      <div className="border-border overflow-hidden rounded-lg border">
        <div className="border-border flex gap-4 border-b px-4 py-2">
          {Array.from(
            { length: LLM_PROFILES_TABLE_COLUMN_COUNT },
            (_, column) => (
              <Skeleton key={column} className="h-4 flex-1" />
            ),
          )}
        </div>
        {Array.from(
          { length: LLM_PROFILES_PLACEHOLDER_ROW_COUNT },
          (_, row) => (
            <div key={row} className="flex gap-4 px-4 py-2.5">
              {Array.from(
                { length: LLM_PROFILES_TABLE_COLUMN_COUNT },
                (_, column) => (
                  <Skeleton key={column} className="h-4 flex-1" />
                ),
              )}
            </div>
          ),
        )}
      </div>
    </PendingRegion>
  );
}
